// EquipmentPanel.jsx — Equipment / Capability walk-down. Controlled 186-item
// pick-list (AISC Fab/Field Capability Inventory) lets the Receiver/Foreman
// record what the shop has. Quiet: no dashboards, no KPIs.
import React, { useEffect, useState, useCallback } from "react";
import {
  Card, Stack, Chip, Box, Typography, CircularProgress, Button, Alert, Snackbar,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, TextField,
  Select, MenuItem, InputLabel, FormControl, Grid,
} from "@mui/material";
import { AddRounded } from "@mui/icons-material";
import ApiCall from "../../utils/apicall";

import SmartTable from "../SmartTable/SmartTable";
const AREAS = [
  { value: "shop", label: "Shop Fabrication" },
  { value: "field", label: "Field Erection" },
  { value: "inspection", label: "Inspection / Test" },
];
const areaLabel = (v) => (AREAS.find((a) => a.value === v) || {}).label || v || "—";
const IN_HOUSE = ["Y", "N", "Sub", "Partial"];
const CONDITIONS = ["Good", "Fair", "Poor", "NA"];
const CAL = ["Current", "Due", "Overdue", "NA", "NotReq"];
const calColor = (c) => (c === "Overdue" ? "error" : c === "Due" ? "warning" : c === "Current" ? "success" : "default");
const blankForm = {
  catalog_code: "", area: "", grp: "", name: "", capacity_metric: "",
  in_house: "Y", qty: "", make_model: "", rated_spec: "",
  condition_rating: "Good", cal_status: "", last_verified: "", verified_by: "", notes: "",
};

export default function EquipmentPanel({ refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [area, setArea] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, c] = await Promise.all([
        ApiCall.get("/inventory/equipment"),
        ApiCall.get("/inventory/equipment/catalog"),
      ]);
      setItems(l?.data?.data?.items || []);
      setCatalog(c?.data?.data?.items || []);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const counts = items.reduce((a, it) => ((a[it.area] = (a[it.area] || 0) + 1), a), {});
  const shown = area === "all" ? items : items.filter((it) => it.area === area);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openAdd = () => { setForm(blankForm); setErr(null); setOpen(true); };
  const pickItem = (val) => {
    if (val && typeof val === "object") {
      setForm((f) => ({ ...f, catalog_code: val.catalog_code, name: val.name, area: val.area, grp: val.grp, capacity_metric: val.capacity_metric || "" }));
    } else if (typeof val === "string") {
      setForm((f) => ({ ...f, catalog_code: "", name: val }));
    }
  };
  const save = async () => {
    if (!form.name.trim()) { setErr("Pick or type an item first."); return; }
    setSaving(true); setErr(null);
    try {
      await ApiCall.post("/inventory/equipment", form);
      setMsg("Equipment recorded.");
      setOpen(false);
      load();
    } catch (e) {
      setErr(e?.response?.data?.message || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <Card sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`All (${items.length})`} color={area === "all" ? "primary" : "default"}
            variant={area === "all" ? "filled" : "outlined"} onClick={() => setArea("all")} />
          {AREAS.map((a) => (
            <Chip key={a.value} label={`${a.label} (${counts[a.value] || 0})`}
              color={area === a.value ? "primary" : "default"}
              variant={area === a.value ? "filled" : "outlined"} onClick={() => setArea(a.value)} />
          ))}
        </Stack>
        <Button variant="contained" startIcon={<AddRounded />} onClick={openAdd}>Record Equipment</Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : shown.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
          <Typography>No equipment recorded yet. Use <strong>Record Equipment</strong> to walk the shop.</Typography>
        </Box>
      ) : (
        <TableContainer>
          <SmartTable tableId="equipment" lockedColumns={["Code", "Item", "Calibration"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell><b>Code</b></TableCell><TableCell><b>Item</b></TableCell>
                <TableCell><b>Area</b></TableCell><TableCell><b>In-House</b></TableCell>
                <TableCell><b>Qty</b></TableCell><TableCell><b>Make / Model</b></TableCell>
                <TableCell><b>Condition</b></TableCell><TableCell><b>Calibration</b></TableCell>
                <TableCell><b>Verified</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((it) => (
                <TableRow key={it.id} hover>
                  <TableCell>{it.catalog_code || "—"}</TableCell>
                  <TableCell>{it.name}{it.grp ? <Typography variant="caption" display="block" color="text.secondary">{it.grp}</Typography> : null}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={areaLabel(it.area)} /></TableCell>
                  <TableCell>{it.in_house || "—"}</TableCell>
                  <TableCell>{it.qty ?? "—"}</TableCell>
                  <TableCell>{it.make_model || "—"}</TableCell>
                  <TableCell>{it.condition_rating || "—"}</TableCell>
                  <TableCell>{it.cal_status ? <Chip size="small" color={calColor(it.cal_status)} label={it.cal_status} /> : "—"}</TableCell>
                  <TableCell>{it.last_verified ? String(it.last_verified).slice(0, 10) : "—"}{it.verified_by ? <Typography variant="caption" display="block" color="text.secondary">{it.verified_by}</Typography> : null}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </SmartTable>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Record Equipment</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              freeSolo options={catalog}
              groupBy={(o) => areaLabel(o.area)}
              getOptionLabel={(o) => (typeof o === "string" ? o : `${o.catalog_code} · ${o.name}`)}
              onChange={(_, val) => pickItem(val)}
              onInputChange={(_, val, reason) => { if (reason === "input") set("name", val); }}
              renderInput={(p) => <TextField {...p} label="Item (pick from the 186-item catalog, or type)" placeholder="Start typing: crane, drill line, UT detector…" />}
            />
            {form.capacity_metric && (
              <Alert severity="info" sx={{ py: 0 }}>What to measure for capacity: <strong>{form.capacity_metric}</strong></Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small"><InputLabel>In-House?</InputLabel>
                  <Select label="In-House?" value={form.in_house} onChange={(e) => set("in_house", e.target.value)}>
                    {IN_HOUSE.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select></FormControl>
              </Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Qty" value={form.qty} onChange={(e) => set("qty", e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Make / Model" value={form.make_model} onChange={(e) => set("make_model", e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Rated Capacity / Spec" value={form.rated_spec} onChange={(e) => set("rated_spec", e.target.value)} /></Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small"><InputLabel>Condition</InputLabel>
                  <Select label="Condition" value={form.condition_rating} onChange={(e) => set("condition_rating", e.target.value)}>
                    {CONDITIONS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select></FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small"><InputLabel>Calibration</InputLabel>
                  <Select label="Calibration" value={form.cal_status} onChange={(e) => set("cal_status", e.target.value)}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    {CAL.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select></FormControl>
              </Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Last Verified" InputLabelProps={{ shrink: true }} value={form.last_verified} onChange={(e) => set("last_verified", e.target.value)} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Verified By" value={form.verified_by} onChange={(e) => set("verified_by", e.target.value)} /></Grid>
              <Grid item xs={12}><TextField fullWidth size="small" label="Notes / Gaps" multiline minRows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Grid>
            </Grid>
            {err && <Alert severity="error">{err}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!msg} autoHideDuration={4000} onClose={() => setMsg(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="success" onClose={() => setMsg(null)}>{msg}</Alert>
      </Snackbar>
    </Card>
  );
}
